import { cn } from '@/lib/cn';
import { Input } from '@heidi/design-system/input';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations, type TranslationKey } from '@heidi/runtime/i18n';
import { Controller, type Control } from 'react-hook-form';
import { type EmailFormData } from '../utils';

interface EmailInputFieldProps {
  control: Control<EmailFormData>;
  isOnboardingRoute?: boolean;
}

/**
 * Shared email input field component for sign-in modals
 */
export function EmailInputField({ control, isOnboardingRoute }: EmailInputFieldProps) {
  const { t } = useTranslations();

  return (
    <div className="flex w-full flex-col gap-1">
      <TypographyP2>{t('auth.email')}</TypographyP2>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => {
          const showError = !!fieldState.error;

          return (
            <>
              <Input
                {...field}
                id={isOnboardingRoute ? 'email' : 'username'}
                name={isOnboardingRoute ? 'email' : 'username'}
                placeholder={t('auth.enterYourEmail')}
                type="email"
                autoComplete={isOnboardingRoute ? 'email' : 'username'}
                data-testid="sign-in-email-input"
                wrapperClassName={cn(
                  showError &&
                    'border-validation-destructive focus-visible:ring-validation-destructive'
                )}
              />
              {showError && fieldState.error?.message && (
                <TypographyCaption className="text-validation-destructive">
                  {t(fieldState.error.message as TranslationKey)}
                </TypographyCaption>
              )}
            </>
          );
        }}
      />
    </div>
  );
}
